import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axiosInstance from "../config/axios";

const DEFAULT_PAGE_SIZE = 10;
const VALID_PAGE_SIZES = new Set([10, 25, 50, 100]);

interface DesignPreferencesContextValue {
	defaultPageSize: number;
	setDefaultPageSize: (value: number) => void;
	loading: boolean;
}

const DesignPreferencesContext = createContext<DesignPreferencesContextValue>({
	defaultPageSize: DEFAULT_PAGE_SIZE,
	setDefaultPageSize: () => undefined,
	loading: false,
});

const parsePageSize = (rawValue: unknown): number => {
	const parsed = Number.parseInt(String(rawValue), 10);
	if (Number.isNaN(parsed) || !VALID_PAGE_SIZES.has(parsed)) {
		return DEFAULT_PAGE_SIZE;
	}
	return parsed;
};

export const DesignPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [defaultPageSize, setDefaultPageSizeState] = useState<number>(DEFAULT_PAGE_SIZE);
	const [loading, setLoading] = useState(false);

	const setDefaultPageSize = useCallback((value: number) => {
		setDefaultPageSizeState(parsePageSize(value));
	}, []);

	const fetchPreferences = useCallback(async () => {
		const partyId = localStorage.getItem("selectedPartyId");
		if (!partyId) {
			setDefaultPageSizeState(DEFAULT_PAGE_SIZE);
			return;
		}

		setLoading(true);
		try {
			const response = await axiosInstance.get(`/design/settings/${partyId}`);
			if (response.data?.success) {
				setDefaultPageSizeState(parsePageSize(response.data.data?.default_page_size));
			} else {
				setDefaultPageSizeState(DEFAULT_PAGE_SIZE);
			}
		} catch {
			setDefaultPageSizeState(DEFAULT_PAGE_SIZE);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchPreferences();
	}, [fetchPreferences]);

	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === "selectedPartyId") {
				void fetchPreferences();
			}
		};

		window.addEventListener("storage", handleStorageChange);
		return () => window.removeEventListener("storage", handleStorageChange);
	}, [fetchPreferences]);

	const value = useMemo(
		() => ({
			defaultPageSize,
			setDefaultPageSize,
			loading,
		}),
		[defaultPageSize, setDefaultPageSize, loading]
	);

	return <DesignPreferencesContext.Provider value={value}>{children}</DesignPreferencesContext.Provider>;
};

export const useDesignPreferences = (): DesignPreferencesContextValue => useContext(DesignPreferencesContext);

